import React, { useEffect, useState } from 'react';
import {
  Alert,
  Grid,
  IconButton,
  Link as MuiLink,
  Snackbar,
  styled,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  CreditCard,
  Delete,
  Edit,
  HelpOutline,
  Link,
  LocalAtm,
  Loyalty,
  Percent,
  PointOfSale,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { includes } from 'lodash';
import { PAYMENT_LINE_CARD_COLOR, PAYMENT_LINE_CASH_COLOR } from './paymentLineColors';
import {
  AccountData,
  DiscountOut,
  DiscountService,
  DiscountType,
  ExtraPaymentLineData,
  ExtraPaymentMode,
  OrderData,
  OrderService,
  OrderStatus,
  PaymentEntityType,
  PaymentLinkOut,
  PaymentLinkPaymentStatus,
  PaymentLinkService,
  PaymentLinkType,
  RefundablePaymentData,
  RefundDisabledReason,
  RefundOverviewData,
  VoucherOut,
  VoucherService,
} from '../api';
import RotatingLoader from './ui/RotatingLoader';
import ConfirmationDialog from './ui/ConfimationDialog';
import { useSafeExecute } from './apiNotifications/safeExecute';
import { EditExtraPaymentLineDialog } from './EditExtraPaymentLineDialog';
import { useAuth } from '../session/InternalAuthProvider';
import TextWithTooltip from './ui/TextWithTooltip';
import { getLinkToPaymentLink, getPaymentLinkStatus } from '../calendar/dialogs/EditOrderDialog/OrderDetails';
import { priceFormatterFromCurrency, usePriceFormatter } from './usePriceFormatter';
import { getDateFormat } from './getDateAndTimeFormat';
import { getCurrencySymbol } from '../utils/utils';
import { getOrderPage } from '../utils/AccountUtils';
import { orderUsesDeposit } from './depositUtils';
import { useCalendar } from '../calendar/CalendarContext/CalendarContext';
import { ReactComponent as MonetaryVoucherIcon } from '../images/voucherIcons/mb-voucher-monetary.svg';
import { ReactComponent as PaymentLinkCopyIcon } from '../images/paymentLinkIcons/mb-pl-copy-link.svg';
import { ReactComponent as PaymentLinkArchiveIcon } from '../images/paymentLinkIcons/mb-pl-archive.svg';
import { RefundIconButton } from '../refunds/RefundIconButton';
import { useRefundAmountFormatter } from '../refunds/refundFormat';
import { findRefundItem } from '../refunds/useRefundOverview';
import { formatMoney } from './bookingFee';

const StyledTableCell = styled(TableCell)({
  padding: 0,
  paddingInlineEnd: 8,
});

const LabelCell = styled(StyledTableCell)({
  width: '55%',
});

const calculateDiscount = (price: number, discount?: DiscountOut) => {
  if (!discount) {
    return price;
  }
  if (discount?.type === DiscountType.PERCENT) {
    return ((100 - (discount.value || 0)) * price) / 100;
  }
  if (discount?.type === DiscountType.FIXED) {
    return price - discount.value;
  }
  return price;
};
const calculateTotalPrice = (
  extraPaymentsLines: ExtraPaymentLineData[],
  paymentLinks: PaymentLinkOut[],
  initialPrice: number,
  workshopPaidWithPaymentLink: boolean,
  voucherAmount: number,
  remainderPaidWithPaymentLink: boolean,
  discount?: DiscountOut,
) => {
  const extraPaymentLinesPrice = (extraPaymentsLines || []).reduce(
    (total: number, obj: ExtraPaymentLineData) => total + obj.price,
    0,
  );
  const paymentLinksPrice = paymentLinks.reduce((total: number, obj: PaymentLinkOut) => total + obj.price, 0);
  const total = extraPaymentLinesPrice + paymentLinksPrice;

  const discountedPrice = calculateDiscount(initialPrice, discount);

  if (remainderPaidWithPaymentLink) {
    return total + voucherAmount;
  }
  return workshopPaidWithPaymentLink ? total : total + discountedPrice;
};

type UsePriceRendererProps = {
  orderOrVoucher: OrderData | VoucherOut;
  initialPrice: number;
  returningCustomerFromOrderId?: string;
  extraPaymentLines: ExtraPaymentLineData[];
  isVoucher?: boolean;
  shouldAllowPriceOverride?: boolean;
  onEditPrice?: () => void;
  // When set, manually recorded extra payment lines get edit and delete buttons.
  onOrderCommitted?: (freshOrder: OrderData) => void;
  // Bump to force a payment-links refetch. This hook is instantiated more than once per
  // order (SlimPrice and FullPriceBreakdown hold independent state), and the fetch is
  // otherwise keyed only on the order id, so a newly created link would never show up.
  refreshKey?: number;
  refundOverview?: RefundOverviewData;
  onRefund?: (item: RefundablePaymentData) => void;
};
type UsePriceRendererResult = {
  SlimPrice: () => JSX.Element;
  FullPriceBreakdown: ({
    isCustomisingPrice,
    calculatedFullPrice,
  }: {
    isCustomisingPrice: boolean;
    calculatedFullPrice?: number;
  }) => JSX.Element;
};
export const usePriceRenderer = ({
  orderOrVoucher,
  initialPrice,
  returningCustomerFromOrderId,
  extraPaymentLines,
  isVoucher,
  shouldAllowPriceOverride,
  onEditPrice,
  onOrderCommitted,
  refreshKey,
  refundOverview,
  onRefund,
}: UsePriceRendererProps): UsePriceRendererResult => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { longDateFormat } = getDateFormat(account);
  const [isLoadingDiscount, setIsLoadingDiscount] = useState(false);
  const [isLoadingPaymentLinks, setIsLoadingPaymentLinks] = useState(false);
  const [isLoadingOriginalOrder, setIsLoadingIOriginalOrder] = useState(false);
  const [discount, setDiscount] = useState<DiscountOut | undefined>(undefined);
  const [originalOrderForReturningCustomer, setOriginalOrderForReturningCustomer] = useState<OrderData | undefined>(
    undefined,
  );
  const [paymentLinks, setPaymentLinks] = useState<PaymentLinkOut[]>([]);
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
  const [lineToEdit, setLineToEdit] = useState<ExtraPaymentLineData | undefined>(undefined);
  const [lineToDelete, setLineToDelete] = useState<ExtraPaymentLineData | undefined>(undefined);
  const safeExecute = useSafeExecute();
  const { openEditOrderDrawer } = useCalendar();
  const [paidVoucher, setPaidVoucher] = useState<VoucherOut | undefined>(undefined);
  const paidVoucherId = isVoucher ? undefined : (orderOrVoucher as OrderData)?.voucher_id;
  const paidVoucherAmount = isVoucher ? undefined : (orderOrVoucher as OrderData)?.voucher_amount;
  useEffect(() => {
    if (paidVoucherId && paidVoucherAmount) {
      void VoucherService.getVoucherById(paidVoucherId).then(setPaidVoucher);
    }
  }, [paidVoucherId, paidVoucherAmount]);
  const openVoucherDrawer = async (voucherId: string) => {
    const voucher = await VoucherService.getVoucherById(voucherId);
    const [listedVoucher] = await VoucherService.getVouchers(1, 1, voucher.code);
    openEditOrderDrawer({
      order: undefined,
      orderPageId: voucher.order_page_id,
      createVoucher: false,
      voucherToEdit: listedVoucher ?? voucher,
    });
  };
  const priceFormatter = usePriceFormatter();
  const formatRefundAmount = useRefundAmountFormatter();
  const workshopPaidWithPaymentLink =
    paymentLinks.find((x) => x.payment_link_type === PaymentLinkType.MAIN_PAYMENT && x.price === initialPrice) !==
    undefined;
  const isLoadingResources = isLoadingDiscount || isLoadingPaymentLinks || isLoadingOriginalOrder;
  useEffect(() => {
    const getDiscount = async () => {
      if (orderOrVoucher?.discount_id) {
        setIsLoadingDiscount(true);
        // currently supported just percentage
        const discountOut = await DiscountService.getDiscountById(orderOrVoucher.discount_id);
        setDiscount(discountOut);
        setIsLoadingDiscount(false);
      }
    };

    void getDiscount();
  }, [orderOrVoucher?.discount_id]);

  useEffect(() => {
    const getOriginalOrderForReturningCustomer = async () => {
      if (returningCustomerFromOrderId) {
        setIsLoadingIOriginalOrder(true);
        // currently supported just percentage

        const originalOrder = await OrderService.getOrder(returningCustomerFromOrderId);
        setOriginalOrderForReturningCustomer(originalOrder);
        setIsLoadingIOriginalOrder(false);
      }
    };
    void getOriginalOrderForReturningCustomer();
  }, [returningCustomerFromOrderId]);
  const getPaymentLinks = async () => {
    if (
      (orderOrVoucher as OrderData).has_extra_payment_links ||
      includes(
        [OrderStatus.PAYMENT_LINK_GENERATED, OrderStatus.PAID_WITH_PAYMENT_LINK],
        (orderOrVoucher as OrderData).status,
      )
    ) {
      setIsLoadingPaymentLinks(true);

      const paymentLinks = await PaymentLinkService.getPaymentLinkByOrderId(orderOrVoucher.id);
      setPaymentLinks(
        paymentLinks.filter((x) => !x.archived && x.payment_link_type !== PaymentLinkType.RESCHEDULE_FEE),
      );

      setIsLoadingPaymentLinks(false);
    }
  };
  useEffect(() => {
    if (orderOrVoucher?.id && !isVoucher) {
      void getPaymentLinks();
    }
  }, [orderOrVoucher?.id, refreshKey]);

  const order = isVoucher ? undefined : (orderOrVoucher as OrderData);
  const hasOrderDeposit = !!order && order.status === OrderStatus.DEPOSIT_PAID && order.deposit_price != null;
  const hasBackendDeposit = orderUsesDeposit(order);
  const depositAmount = hasBackendDeposit ? order!.deposit_price : undefined;
  const hasAnyDeposit = hasOrderDeposit || !!account.deposit_percentage;
  const orderPage = order ? getOrderPage(account, order.order_page_id) : undefined;
  const depositCurrency = orderPage?.deposit_currency;
  const depositCurrencyMismatch = depositCurrency && depositCurrency !== account.currency;
  const formatDepositPrice = (price: number) =>
    depositCurrency ? priceFormatterFromCurrency(price, depositCurrency) : priceFormatter(price);

  const voucherAmount = order?.voucher_amount || 0;
  const remainderPaidWithPaymentLink =
    voucherAmount > 0 &&
    paymentLinks.some(
      (paymentLink) =>
        paymentLink.payment_link_type === PaymentLinkType.MAIN_PAYMENT &&
        paymentLink.price === calculateDiscount(initialPrice, discount) - voucherAmount,
    );
  const baseFullyPaidByVoucher = voucherAmount > 0 && !remainderPaidWithPaymentLink;
  const voucherPaymentLink = (
    <Grid container gap={0.5} alignItems="center" flexWrap="nowrap">
      <MonetaryVoucherIcon width={26} height={26} />
      <MuiLink
        component="button"
        type="button"
        variant="body1"
        onClick={() => paidVoucherId && openVoucherDrawer(paidVoucherId)}>
        {paidVoucher
          ? t('calendar.edit_order.order_details.paidWithGiftCardNumber', {
              orderNumber: paidVoucher.order_number,
            })
          : t('calendar.edit_order.order_details.paidWithGiftCard')}
      </MuiLink>
    </Grid>
  );
  const totalPrice = calculateTotalPrice(
    extraPaymentLines,
    paymentLinks,
    initialPrice,
    workshopPaidWithPaymentLink,
    voucherAmount,
    remainderPaidWithPaymentLink,
    discount,
  );
  const baseRefundItem = workshopPaidWithPaymentLink
    ? findRefundItem(refundOverview, PaymentEntityType.PAYMENT_LINK, paymentLinks[0].id)
    : findRefundItem(
        refundOverview,
        isVoucher ? PaymentEntityType.VOUCHER : PaymentEntityType.ORDER,
        orderOrVoucher?.id,
      );
  const renderRefundIcon = (item?: RefundablePaymentData) =>
    onRefund && <RefundIconButton item={item} onRefund={onRefund} />;
  const renderRefundCaptions = (item?: RefundablePaymentData) => {
    const refundedAmount = formatRefundAmount(item?.refunded_amount ?? 0);
    return (
      <>
        {!!item?.refunded_amount && !!refundedAmount && (
          <Typography variant="caption" color="text.secondary">
            {t('refunds.row.refundedPartial', { amount: refundedAmount })}
          </Typography>
        )}
        {item?.disabled_reason === RefundDisabledReason.PENDING && (
          <Typography variant="caption" color="secondary.dark">
            {t('refunds.row.pending')}
          </Typography>
        )}
      </>
    );
  };
  const refundedTotal = refundOverview?.refunded_total ?? 0;
  const bookingFeeTotal = (refundOverview?.payments ?? []).reduce(
    (sum, payment) => sum + (payment.booking_fee_amount ?? 0),
    0,
  );
  const SlimPrice = () => {
    if (isLoadingResources) {
      return <RotatingLoader width={'24px'} />;
    }

    if (hasOrderDeposit && depositAmount) {
      return (
        <Grid container alignItems="center" gap={1}>
          <Tooltip title={t('calendar.edit_order.order_details.depositPaid')} arrow>
            <Typography>{formatDepositPrice(depositAmount)}</Typography>
          </Tooltip>
        </Grid>
      );
    }

    if (!discount && !originalOrderForReturningCustomer) {
      return <Grid>{priceFormatter(totalPrice)}</Grid>;
    }
    if (discount) {
      return (
        <Grid container alignItems="center" gap={1}>
          <Tooltip
            title={
              <Grid container flexDirection="column" gap={-0.5}>
                <Grid item container>
                  <Typography>{`${t('calendar.order_popper.discountCode')}`}</Typography>
                </Grid>
              </Grid>
            }
            arrow>
            <Percent style={{ color: theme.palette.text.disabled }} />
          </Tooltip>
          {priceFormatter(totalPrice)}
        </Grid>
      );
    }
    if (originalOrderForReturningCustomer) {
      return (
        <Grid container alignItems="center" gap={1}>
          <Tooltip
            title={
              <Grid container flexDirection="column" gap={-0.5}>
                <Grid item container gap={1}>
                  <Typography>{`${t('calendar.order_popper.returningCustomerDiscount')}`}</Typography>
                </Grid>
              </Grid>
            }
            arrow>
            <Loyalty style={{ color: theme.palette.text.disabled }} />
          </Tooltip>
          <Typography>{priceFormatter(totalPrice)}</Typography>
        </Grid>
      );
    }
    return <Grid container />;
  };
  const FullPriceBreakdown = ({
    isCustomisingPrice,
    calculatedFullPrice,
  }: {
    isCustomisingPrice: boolean;
    calculatedFullPrice?: number;
  }) => {
    if (isLoadingResources) {
      return <RotatingLoader width={'24px'} />;
    }
    // to allow customising ptice properly without all the interuptions
    if (isCustomisingPrice) {
      return <Grid />;
    }
    return (
      <Grid>
        <Table size="small" stickyHeader>
          <TableBody>
            {!isCustomisingPrice && (
              <TableRow>
                <LabelCell>
                  <Grid container flexDirection="column" flexWrap="nowrap">
                    {(() => {
                      const label = hasOrderDeposit
                        ? t('calendar.edit_order.order_details.depositPaid')
                        : hasBackendDeposit
                        ? account.deposit_percentage
                          ? t('calendar.edit_order.order_details.advance', { perc: account.deposit_percentage })
                          : t('calendar.edit_order.order_details.deposit')
                        : orderPage?.show_price_as_deposit
                        ? t('calendar.edit_order.order_details.deposit')
                        : t('calendar.edit_order.order_details.price');
                      return isSmallScreen ? (
                        <TextWithTooltip text={label.slice(0, 20)} tooltip={label} width={'120'} variant={'body1'} />
                      ) : (
                        <Typography>{label}</Typography>
                      );
                    })()}
                    {renderRefundCaptions(baseRefundItem)}
                  </Grid>
                </LabelCell>
                <StyledTableCell style={{ minWidth: '70px' }}>
                  <Typography>
                    {depositAmount ? formatDepositPrice(depositAmount) : priceFormatter(initialPrice)}
                  </Typography>
                </StyledTableCell>
                <StyledTableCell>
                  <Grid container alignItems="center" flexWrap="nowrap" gap={0.5}>
                    {baseFullyPaidByVoucher && voucherPaymentLink}
                    {workshopPaidWithPaymentLink && (
                      <Grid
                        sx={{ cursor: 'pointer' }}
                        onClick={() => {
                          void navigator.clipboard.writeText(getLinkToPaymentLink(paymentLinks[0].id));
                          setCopySnackbarOpen(true);
                        }}>
                        <Tooltip title={t('calendar.edit_order.order_details.copyPaymentLink')}>
                          <span style={{ display: 'inline-flex' }}>
                            <PaymentLinkCopyIcon width={24} height={24} />
                          </span>
                        </Tooltip>
                      </Grid>
                    )}
                    {workshopPaidWithPaymentLink && (
                      <Grid>{getPaymentLinkStatus(paymentLinks[0].payment_status, t)}</Grid>
                    )}
                    {!isCustomisingPrice && shouldAllowPriceOverride && onEditPrice && (
                      <Grid onClick={onEditPrice} sx={{ cursor: 'pointer' }}>
                        <Edit fontSize="small" />
                      </Grid>
                    )}
                    {renderRefundIcon(baseRefundItem)}
                  </Grid>
                </StyledTableCell>
              </TableRow>
            )}
            {!isCustomisingPrice && hasBackendDeposit && (
              <TableRow>
                <LabelCell>
                  <Typography>{t('calendar.edit_order.order_details.fullPrice')}</Typography>
                </LabelCell>
                <StyledTableCell style={{ minWidth: '70px' }}>
                  <Typography>{priceFormatter(initialPrice)}</Typography>
                </StyledTableCell>
                <StyledTableCell />
              </TableRow>
            )}
            {!isCustomisingPrice && hasAnyDeposit && (
              <TableRow>
                <LabelCell>
                  <Typography fontWeight="700">{t('calendar.edit_order.order_details.remainingBalance')}</Typography>
                </LabelCell>
                <StyledTableCell style={{ minWidth: '70px' }} colSpan={2}>
                  {hasOrderDeposit && depositAmount && depositCurrencyMismatch ? (
                    <Grid container alignItems="center" gap={0.5} flexWrap="nowrap">
                      {priceFormatter(initialPrice)}
                      <Typography>{' - '}</Typography>
                      {formatDepositPrice(depositAmount)}
                    </Grid>
                  ) : (
                    <Typography>
                      {priceFormatter(
                        hasOrderDeposit
                          ? initialPrice - (depositAmount || 0)
                          : calculatedFullPrice
                          ? calculatedFullPrice - initialPrice
                          : 0,
                      )}
                    </Typography>
                  )}
                </StyledTableCell>
              </TableRow>
            )}
            {discount && (
              <TableRow>
                <LabelCell>
                  <Grid container gap={0.5} alignItems="center" flexWrap="wrap">
                    <Typography>{`${t('calendar.order_popper.discountCode')}:`}</Typography>
                    <Typography fontWeight="600">{discount.code}</Typography>
                    <Typography color="text.secondary">{`(${discount.value}${
                      discount.type === DiscountType.PERCENT ? '%' : getCurrencySymbol(account)
                    })`}</Typography>
                  </Grid>
                </LabelCell>
                <StyledTableCell style={{ minWidth: '70px' }} colSpan={2}>
                  <Typography>{priceFormatter(calculateDiscount(initialPrice, discount) - initialPrice)}</Typography>
                </StyledTableCell>
              </TableRow>
            )}
            {remainderPaidWithPaymentLink && (
              <TableRow>
                <LabelCell>{voucherPaymentLink}</LabelCell>
                <StyledTableCell style={{ minWidth: '70px' }} colSpan={2}>
                  <Typography>{priceFormatter(voucherAmount)}</Typography>
                </StyledTableCell>
              </TableRow>
            )}
            {returningCustomerFromOrderId && (
              <TableRow>
                <LabelCell>
                  <Typography>{t('calendar.order_popper.returningCsutomerDiscount')}</Typography>
                </LabelCell>
                <StyledTableCell style={{ minWidth: '70px' }}>
                  <Typography>
                    {priceFormatter(-((account.returning_customer_settings?.value || 0) * initialPrice) / 100)}
                  </Typography>
                </StyledTableCell>
                <StyledTableCell>
                  <Tooltip
                    title={
                      <Grid container flexDirection="column" gap={-0.5}>
                        <Grid item container gap={1}>
                          <Typography>{`${t('calendar.order_popper.value')}:`}</Typography>
                          <Typography>{`${account.returning_customer_settings?.value}%`}</Typography>
                        </Grid>
                        <Grid item container gap={1}>
                          <Typography>{`${t('calendar.order_popper.originalOrder')}:`}</Typography>
                          <Typography>{`#${originalOrderForReturningCustomer?.order_number}`}</Typography>
                        </Grid>
                        <Grid item container gap={1}>
                          <Typography>{`${t('calendar.order_popper.name')}:`}</Typography>
                          <Typography>{`${originalOrderForReturningCustomer?.firstname} ${originalOrderForReturningCustomer?.lastname}`}</Typography>
                        </Grid>
                        <Grid item container gap={1}>
                          <Typography>{`${t('calendar.order_popper.date')}:`}</Typography>
                          <Typography>
                            {moment(originalOrderForReturningCustomer?.date).format(longDateFormat)}
                          </Typography>
                        </Grid>
                      </Grid>
                    }
                    arrow>
                    <HelpOutline style={{ color: theme.palette.text.disabled }} />
                  </Tooltip>
                </StyledTableCell>
              </TableRow>
            )}
            {(extraPaymentLines?.length > 0 || paymentLinks?.length > 0) && (
              <TableRow>
                <Grid mt={1}>
                  <Typography fontWeight="700">{t('calendar.edit_order.order_details.otherItems')}</Typography>
                </Grid>
              </TableRow>
            )}
            {extraPaymentLines?.map((extraPaymentsLine: ExtraPaymentLineData) => {
              const extraLineRefundItem = findRefundItem(
                refundOverview,
                PaymentEntityType.EXTRA_PAYMENT_LINE,
                extraPaymentsLine.id as string,
              );
              return (
                <TableRow>
                  <LabelCell>
                    <Grid container flexDirection="column" flexWrap="nowrap">
                      <Grid container gap={0.5} alignItems="center" flexWrap="nowrap">
                        {/* A provider transaction id means a real charge (POS terminal or saved card);
                          otherwise it was recorded by hand. */}
                        {extraPaymentsLine.voucher_id ? (
                          <Tooltip title={t('calendar.edit_order.order_details.paidWithGiftCardLine')} arrow>
                            <IconButton
                              size="small"
                              sx={{ padding: 0 }}
                              onClick={() => openVoucherDrawer(extraPaymentsLine.voucher_id as string)}>
                              <MonetaryVoucherIcon width={26} height={26} />
                            </IconButton>
                          </Tooltip>
                        ) : extraPaymentsLine.payment_mode === ExtraPaymentMode.POS_TERMINAL ? (
                          <Tooltip title={t('calendar.edit_order.order_details.paidAtPosTerminal')} arrow>
                            <PointOfSale fontSize="small" style={{ color: PAYMENT_LINE_CARD_COLOR }} />
                          </Tooltip>
                        ) : extraPaymentsLine.provider_transaction_id ? (
                          <Tooltip title={t('calendar.edit_order.order_details.chargedToSavedCard')} arrow>
                            <CreditCard fontSize="small" style={{ color: PAYMENT_LINE_CARD_COLOR }} />
                          </Tooltip>
                        ) : (
                          <Tooltip
                            title={
                              extraPaymentsLine.payment_method_label
                                ? t('calendar.edit_order.order_details.recordedManuallyWith', {
                                    method: extraPaymentsLine.payment_method_label,
                                  })
                                : t('calendar.edit_order.order_details.recordedManually')
                            }
                            arrow>
                            <LocalAtm fontSize="small" style={{ color: PAYMENT_LINE_CASH_COLOR }} />
                          </Tooltip>
                        )}
                        {isSmallScreen ? (
                          <TextWithTooltip
                            text={extraPaymentsLine.title?.slice(0, 15) || ''}
                            tooltip={extraPaymentsLine.title || ''}
                            width={'120'}
                            variant={'body1'}
                          />
                        ) : (
                          <Typography>{extraPaymentsLine.title}</Typography>
                        )}
                        {extraPaymentsLine.payment_method_label && (
                          <Typography variant="caption" color="text.secondary" noWrap>
                            · {extraPaymentsLine.payment_method_label}
                          </Typography>
                        )}
                      </Grid>
                      {renderRefundCaptions(extraLineRefundItem)}
                    </Grid>
                  </LabelCell>
                  <StyledTableCell style={{ minWidth: '70px' }}>
                    <Typography>{priceFormatter(extraPaymentsLine.price)}</Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Grid container alignItems="center" flexWrap="nowrap">
                      {onOrderCommitted && extraPaymentsLine.is_manual && !extraPaymentsLine.voucher_id && (
                        <Grid container flexWrap="nowrap" width="auto">
                          <Tooltip title={t('calendar.edit_order.order_details.extraPayment.editLine')} arrow>
                            <IconButton size="small" onClick={() => setLineToEdit(extraPaymentsLine)}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('calendar.edit_order.order_details.extraPayment.deleteLine')} arrow>
                            <IconButton size="small" onClick={() => setLineToDelete(extraPaymentsLine)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Grid>
                      )}
                      {renderRefundIcon(extraLineRefundItem)}
                    </Grid>
                  </StyledTableCell>
                </TableRow>
              );
            })}

            {paymentLinks.map((paymentLink: PaymentLinkOut) => {
              if (
                workshopPaidWithPaymentLink &&
                paymentLink.payment_link_type === PaymentLinkType.MAIN_PAYMENT &&
                paymentLink.price === initialPrice
              ) {
                return null;
              }
              const disableDelete = paymentLink.payment_status === PaymentLinkPaymentStatus.PAID;
              const paymentLinkRefundItem = findRefundItem(
                refundOverview,
                PaymentEntityType.PAYMENT_LINK,
                paymentLink.id,
              );
              return (
                <TableRow>
                  <LabelCell>
                    <Grid container flexDirection="column" flexWrap="nowrap">
                      <Grid container gap={0.5} alignItems="center" flexWrap="nowrap">
                        <Tooltip title={t('calendar.edit_order.order_details.paymentLink')} arrow>
                          <Link fontSize="small" style={{ color: theme.palette.text.disabled }} />
                        </Tooltip>
                        {isSmallScreen ? (
                          <TextWithTooltip
                            text={paymentLink.title?.slice(0, 15) || ''}
                            tooltip={paymentLink.title || ''}
                            width={'120'}
                            variant={'body1'}
                          />
                        ) : (
                          <Typography>{paymentLink.title}</Typography>
                        )}
                      </Grid>
                      {renderRefundCaptions(paymentLinkRefundItem)}
                    </Grid>
                  </LabelCell>
                  <StyledTableCell style={{ minWidth: '70px' }}>
                    <Typography>{priceFormatter(paymentLink.price)}</Typography>
                  </StyledTableCell>
                  <StyledTableCell>
                    <Grid container alignItems="center" flexWrap="nowrap" gap={0.5}>
                      <Grid
                        display="flex"
                        alignItems="center"
                        sx={{ cursor: 'pointer' }}
                        onClick={() => {
                          void navigator.clipboard.writeText(getLinkToPaymentLink(paymentLink.id));
                          setCopySnackbarOpen(true);
                        }}>
                        <Tooltip title={t('calendar.edit_order.order_details.copyPaymentLink')}>
                          <span style={{ display: 'inline-flex' }}>
                            <PaymentLinkCopyIcon width={24} height={24} />
                          </span>
                        </Tooltip>
                        {getPaymentLinkStatus(paymentLink.payment_status, t)}
                      </Grid>
                      <Grid
                        display="flex"
                        alignItems="center"
                        style={{ cursor: 'pointer' }}
                        onClick={async () => {
                          if (!disableDelete) {
                            await PaymentLinkService.archivePaymentLink({ payment_link_id: paymentLink.id });
                            void getPaymentLinks();
                          }
                        }}>
                        <Tooltip
                          title={
                            disableDelete
                              ? t('calendar.edit_order.order_details.deleteDisabled')
                              : t('calendar.edit_order.order_details.delete')
                          }>
                          <span style={{ display: 'inline-flex', opacity: disableDelete ? 0.35 : 1 }}>
                            <PaymentLinkArchiveIcon width={24} height={24} />
                          </span>
                        </Tooltip>
                      </Grid>
                      {renderRefundIcon(paymentLinkRefundItem)}
                    </Grid>
                  </StyledTableCell>
                </TableRow>
              );
            })}
            {bookingFeeTotal > 0 && (
              <TableRow>
                <LabelCell>
                  <Grid container flexDirection="column" flexWrap="nowrap">
                    <Typography color="text.secondary">{t('calendar.edit_order.order_details.bookingFee')}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('calendar.edit_order.order_details.bookingFeeHint')}
                    </Typography>
                  </Grid>
                </LabelCell>
                <StyledTableCell style={{ minWidth: '70px' }}>
                  <Typography color="text.secondary" dir="ltr">
                    {formatMoney(bookingFeeTotal, account)}
                  </Typography>
                </StyledTableCell>
                <StyledTableCell />
              </TableRow>
            )}
            {!hasAnyDeposit && (
              <TableRow>
                <LabelCell>
                  <Typography fontWeight="700">{t('calendar.edit_order.order_details.totalPayments')}</Typography>
                </LabelCell>

                <StyledTableCell style={{ minWidth: '70px' }}>
                  <Typography fontWeight="700">{priceFormatter(totalPrice)}</Typography>
                </StyledTableCell>
                <StyledTableCell />
              </TableRow>
            )}
            {!hasAnyDeposit && refundedTotal > 0 && (
              <>
                <TableRow>
                  <LabelCell>
                    <Typography>{t('refunds.totals.refunds')}</Typography>
                  </LabelCell>
                  <StyledTableCell style={{ minWidth: '70px' }}>
                    <Grid container alignItems="center" flexWrap="nowrap" dir="ltr">
                      <Typography>-</Typography>
                      {priceFormatter(refundedTotal)}
                    </Grid>
                  </StyledTableCell>
                  <StyledTableCell />
                </TableRow>
                <TableRow>
                  <LabelCell>
                    <Typography fontWeight="700">{t('refunds.totals.net')}</Typography>
                  </LabelCell>
                  <StyledTableCell style={{ minWidth: '70px' }}>
                    <Typography fontWeight="700">{priceFormatter(totalPrice - refundedTotal)}</Typography>
                  </StyledTableCell>
                  <StyledTableCell />
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
        {bookingFeeTotal > 0 && !hasAnyDeposit && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {t('calendar.edit_order.order_details.customerPaidTotal', {
              amount: formatMoney(totalPrice + bookingFeeTotal, account),
            })}
          </Typography>
        )}
        <Snackbar
          open={copySnackbarOpen}
          onClose={() => setCopySnackbarOpen(false)}
          autoHideDuration={2000}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert onClose={() => setCopySnackbarOpen(false)} severity="success">
            {t('copiedToClipboard')}
          </Alert>
        </Snackbar>
        {lineToEdit && onOrderCommitted && (
          <EditExtraPaymentLineDialog
            orderId={orderOrVoucher.id}
            line={lineToEdit}
            onClose={() => setLineToEdit(undefined)}
            onCommitted={onOrderCommitted}
          />
        )}
        {lineToDelete && onOrderCommitted && (
          <ConfirmationDialog
            isOpen
            title={t('calendar.edit_order.order_details.extraPayment.deleteLine')}
            content={t('calendar.edit_order.order_details.extraPayment.deleteLineConfirm', {
              title: lineToDelete.title,
            })}
            onClose={async (isConfirmed) => {
              if (isConfirmed) {
                try {
                  const freshOrder = await safeExecute({
                    apiCall: () => OrderService.removeOrderExtraPayment(orderOrVoucher.id, lineToDelete.id!),
                    successMessage: t('calendar.edit_order.order_details.extraPayment.lineDeleted'),
                  });
                  onOrderCommitted(freshOrder);
                } catch {
                  // safeExecute already surfaced the failure.
                }
              }
              setLineToDelete(undefined);
            }}
          />
        )}
      </Grid>
    );
  };

  return { SlimPrice, FullPriceBreakdown };
};
